import React, { useEffect, useImperativeHandle } from 'react';

// third party
import MapboxDraw, { MapboxDrawOptions } from '@mapbox/mapbox-gl-draw';
import { MapRef, useControl, useMap } from 'react-map-gl/mapbox';
import { FeatureCollection } from 'geojson';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

const useEventHandler = <
  Event extends
    | MapboxDraw.DrawCreateEvent
    | MapboxDraw.DrawUpdateEvent
    | MapboxDraw.DrawDeleteEvent,
>(
  map: MapRef | undefined,
  eventName: string,
  eventHandler?: (event: Event) => void,
) => {
  useEffect(() => {
    if (eventHandler) {
      map?.on(eventName, eventHandler);

      return () => {
        map?.off(eventName, eventHandler);
      };
    }
  }, [map, eventName, eventHandler]);
};

interface DrawControlProps extends MapboxDrawOptions {
  features?: FeatureCollection['features'];
  onCreate: (event: MapboxDraw.DrawCreateEvent) => void;
  onUpdate: (event: MapboxDraw.DrawUpdateEvent) => void;
  onDelete: (event: MapboxDraw.DrawDeleteEvent) => void;
}

const DrawControl = React.forwardRef<MapboxDraw, DrawControlProps>(
  (
    { features, onCreate, onUpdate, onDelete, ...props }: DrawControlProps,
    ref,
  ) => {
    const map = useMap().current;
    const draw = useControl(() => new MapboxDraw(props));

    useImperativeHandle(ref, () => draw);

    useEffect(() => {
      draw.set({
        type: 'FeatureCollection',
        features: features ?? [],
      });

      // `set` will replace all features effectively unselecting them
      // reselect all features
      draw.changeMode('simple_select', {
        featureIds: draw
          .getAll()
          .features.map(feature => feature.id)
          .filter((id): id is string | number => id !== undefined)
          .map(id => id.toString()),
      });
    }, [draw, features]);

    useEventHandler(map, 'draw.create', onCreate);
    useEventHandler(map, 'draw.update', onUpdate);
    useEventHandler(map, 'draw.delete', onDelete);

    return null;
  },
);
DrawControl.displayName = 'DrawControl';

export { DrawControl };
